import { store } from "@app/store"
import {
  Configuration,
  ClassApi,
  UserDto,
  ChangeProgramClassRequest,
  ProgramClassDto,
} from "@masterschool/course-builder-api"
import { selectAccessToken } from "@features/login/loginSelectors"
import { CampusClient } from "./campusClient"

// One active enrollment of a student, as shown in the manage-programs UI.
// Resolved to the program behind the offering, so the domain/type/language +
// start date drive a clear, sortable label (see formatProgramLabel).
// offeringClientId is the membership that remove acts on.
export type StudentCourseClass = {
  courseClassId: string
  courseClassName: string
  language: string
  startDate: string
}

export type StudentProgram = {
  offeringClientId: string
  domain: string
  type: string
  language: string
  startDate: string
  marketingDescription: string
  classes: StudentCourseClass[]
}

export type StudentProgramsResponse = {
  cards: StudentProgram[]
  unattributedClasses: StudentCourseClass[]
}

export class ProgramClassClient {
  static classClient = new ClassApi(
    new Configuration({
      basePath: `${import.meta.env.VITE_BASE_URL ?? ""}/api`,
    }),
  )

  static makeHeaders() {
    return {
      headers: {
        Authorization: `Bearer ${selectAccessToken(store.getState())}`,
      },
    }
  }

  static async registerStudentToClass(
    classId: string,
    student: Omit<UserDto, "id" | "roles">,
    language: string | undefined,
  ) {
    const request = { ...student, language }
    return ProgramClassClient.classClient
      .programClassControllerRegisterStudent(
        request,
        classId,
        ProgramClassClient.makeHeaders(),
      )
      .then((response) => response.data)
      .catch((error) => {
        if (error.response?.status === 409) {
          throw new Error(error.response.data.message)
        }
        throw error
      })
  }

  static async studentRegistrationStatus(studentEmail: string) {
    return ProgramClassClient.classClient
      .programClassControllerUserRegistrationStatus(
        studentEmail,
        ProgramClassClient.makeHeaders(),
      )
      .then((response) => response.data)
  }

  // The generated client has no slot for the class id, and regenerating it for
  // one field is not worth it — the same reason add/remove-from-program bypass it.
  static async unregisterStudentFromClass(studentId: string, classId?: string) {
    return fetch(
      `${
        import.meta.env.VITE_BASE_URL ?? ""
      }/api/v1/classes/${studentId}/unregister`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...ProgramClassClient.makeHeaders().headers,
        },
        body: JSON.stringify({ classId }),
      },
    ).then((response) => {
      if (!response.ok) {
        throw new Error("Failed to drop the student out")
      }
    })
  }

  static async createProgramClass(
    name: string,
    programId: string,
    syllabusId?: string,
  ) {
    return ProgramClassClient.classClient
      .programClassControllerCreateProgramClass(
        {
          name,
          programId,
          syllabusId,
        },
        ProgramClassClient.makeHeaders(),
      )
      .then((response) => response.data)
  }

  static async editProgramClass(req: {
    classId: string
    name: string
    syllabusId: string | undefined
  }) {
    return ProgramClassClient.classClient
      .programClassControllerEditProgramClass(
        req,
        req.classId,
        ProgramClassClient.makeHeaders(),
      )
      .then((response) => response.data)
  }

  // Raw fetch: the generated client's ChangeProgramClassRequest predates
  // sourceClassId, and regenerating the package for one optional field isn't
  // worth it — the same reason add/remove-from-program bypass it.
  //
  // sourceClassId says WHICH class (offering) the student is leaving. Without it
  // campus falls back to their most-recently-created active registration, which
  // for a multi-program student can be a program they are not moving out of.
  static async changeStudentClass(
    request: ChangeProgramClassRequest & { sourceClassId?: string },
  ): Promise<void> {
    const basePath = `${import.meta.env.VITE_BASE_URL ?? ""}/api`
    const response = await fetch(`${basePath}/v1/classes/change-class`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${selectAccessToken(store.getState())}`,
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      throw new Error(
        `Failed to change the student's class (${response.status})`,
      )
    }
  }

  // Manage-programs UI. Raw fetch because these endpoints post-date the
  // generated client.
  static async getStudentPrograms(
    userClientId: string,
  ): Promise<StudentProgramsResponse> {
    const basePath = `${import.meta.env.VITE_BASE_URL ?? ""}/api`
    const response = await fetch(
      `${basePath}/v1/classes/student/${userClientId}/programs`,
      {
        headers: {
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to load the student's programs (${response.status})`,
      )
    }
    return response.json()
  }

  static async removeStudentFromProgram(request: {
    studentId: string
    offeringClientId: string
    summary: string
  }): Promise<void> {
    const basePath = `${import.meta.env.VITE_BASE_URL ?? ""}/api`
    const response = await fetch(
      `${basePath}/v1/classes/student/remove-from-program`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
        body: JSON.stringify(request),
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to remove the student from the program (${response.status})`,
      )
    }
  }

  static async addStudentToProgram(request: {
    studentId: string
    offeringClientId: string
    programId?: string
    language: string | undefined
  }): Promise<void> {
    const basePath = `${import.meta.env.VITE_BASE_URL ?? ""}/api`
    const response = await fetch(
      `${basePath}/v1/classes/student/add-to-program`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${selectAccessToken(store.getState())}`,
        },
        body: JSON.stringify(request),
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to add the student to the program (${response.status})`,
      )
    }
  }

  static async deleteProgramClass(classId: string): Promise<Date> {
    return CampusClient.delete(`/school/${classId}`, {})
  }

  static async getClassById(classId: string): Promise<ProgramClassDto> {
    return CampusClient.get(`/school/${classId}`)
  }
}
